/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import {
  ActionInvokeCloudFunctionItem,
  defaultValue,
} from './ActionInvokeCloudFunction';

const ActionInvokeCloudFunctionManager: ActionManagerFn<
  'ActionInvokeCloudFunction'
> = (platform: Platform): ActionManager<'ActionInvokeCloudFunction'> => {
  let callback = emptyFunction;
  return {
    ...baseActionManager(platform, 'ActionInvokeCloudFunction'),
    getDisplayText: (action) => action.props.functionUri ?? '',
    isChildAllowed: (page, item, childType) =>
      ['ActionOnResponse', 'ActionSetParam'].includes(childType),
    defaultValue,
    defaultChildren: ['ActionSetParam', 'ActionSetParam'],
    props: {
      ...baseActionProps,
      fnAppUid: {
        label: 'App',
        type: 'AppUidChooser',
        helpText: (item: ActionInvokeCloudFunctionItem) => {
          if (item.props.fnAppUid === platform.appUid) {
            return 'Warning! When you migrate this page to another app, it will invoke the function in this app. Leave this field blank otherwise.';
          }
          return 'Leave blank to use the current app. The function in the selected app will be invoked.';
        },
      },
      functionUri: {
        label: 'Function',
        type: 'FunctionURIChooser',
        required: true,
        refreshCallback(cb) {
          callback = cb;
        },
        getAppUid(page, item, context) {
          return (item as ActionInvokeCloudFunctionItem).props.fnAppUid;
        },
      },
      fileDownload: {
        label: 'For File Download',
        type: 'boolean',
        helpText: `Enable this if your cloud function is used for file download.

e.g. Cloud Function
\`\`\`javascript
// set the file name on the header
response.header("Content-Disposition", 'attachment; filename="test.csv"');
// allow it to be read by this action
response.header("Access-Control-Expose-Headers", 'Content-Disposition');
// set the file content
response.contentType("application/csv");
response.body("...");
\`\`\`
`,
      },
      openInNewTab: {
        label: 'Open In New Tab',
        type: 'boolean',
        helpText: 'Enable this to open the function in New Tab.',
      },
      fileNameExpression: {
        label: 'File Name',
        type: 'expression',
        returnType: async () => 'string',
        helpText:
          "File name to be used when it's not available on the response's Content-Disposition header.",
      },
    },
    isPropertyVisible(prop, props): boolean {
      switch (prop) {
        case 'fileNameExpression':
          return !!props.fileDownload;
        default:
          return true;
      }
    },
    onPropertyChange: (property, value) => {
      if (property === 'fnAppUid') {
        callback();
        return {
          [property]: value,
          functionUri: '',
        };
      }
      if (property === 'openInNewTab') {
        callback();
        return {
          [property]: value,
          fileDownload: false,
        };
      }
      if (property === 'fileDownload') {
        callback();
        return {
          [property]: value,
          openInNewTab: false,
        };
      }
      return {
        [property]: value,
      };
    },
    replaceableWith: ['ActionInvokeControllerFunction'],
    name: 'Invoke Cloud Function',
    description: `
Use this Action to invoke a **Cloud Function** securely.
        
You can post JSON body payload to the Cloud Function using one or more Set Param child actions.
        
#### Sample Cloud Function
\`\`\`javascript
// all the parameters set using \`Set Param\` action will be available under request.body
const { param1, param2 } = request.body;

// respond back with any JSON Object
response.json({cfParam1: "Hello", cfParam2: "World"});
\`\`\`

#### On Response

Use \`On Response\` child action to perform further actions after receiving the response from the Cloud Function call.

\`\`\`javascript
// The JSON Object from the server will be available as $._.response
// Sample expression using the above values sent by the server
\`\${$._.response.cfParam1} \${$._.response.cfParam2}!\` // Hello World!
\`\`\`

`,
    icon: { iconName: 'bolt' },
  };
};

export default ActionInvokeCloudFunctionManager;
